import { cn } from '@/lib/utils';

interface ProgressIndicatorProps {
  percentage: number;
  currentStep?: number | undefined;
  totalSteps?: number | undefined;
  currentOperation?: string | undefined;
  size?: 'sm' | 'md' | 'lg' | undefined;
  className?: string | undefined;
}

const sizeConfig = {
  sm: { diameter: 80, strokeWidth: 6, textSize: 'text-lg' },
  md: { diameter: 120, strokeWidth: 8, textSize: 'text-2xl' },
  lg: { diameter: 160, strokeWidth: 10, textSize: 'text-3xl' },
};

export function ProgressIndicator({
  percentage,
  currentStep,
  totalSteps,
  currentOperation,
  size = 'md',
  className,
}: ProgressIndicatorProps) {
  const config = sizeConfig[size];
  const radius = (config.diameter - config.strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className={cn('flex flex-col items-center gap-4', className)}>
      {/* Circular Progress */}
      <div className="relative" style={{ width: config.diameter, height: config.diameter }}>
        <svg
          className="transform -rotate-90"
          width={config.diameter}
          height={config.diameter}
        >
          {/* Background circle */}
          <circle
            className="text-muted stroke-current"
            strokeWidth={config.strokeWidth}
            fill="none"
            r={radius}
            cx={config.diameter / 2}
            cy={config.diameter / 2}
          />
          {/* Progress circle */}
          <circle
            className="text-primary stroke-current transition-all duration-500 ease-out"
            strokeWidth={config.strokeWidth}
            strokeLinecap="round"
            fill="none"
            r={radius}
            cx={config.diameter / 2}
            cy={config.diameter / 2}
            style={{
              strokeDasharray: circumference,
              strokeDashoffset,
            }}
          />
        </svg>

        {/* Percentage text */}
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={cn('font-bold', config.textSize)}>
            {Math.round(percentage)}%
          </span>
        </div>
      </div>

      {/* Step counter */}
      {currentStep !== undefined && totalSteps !== undefined && (
        <div className="text-sm text-muted-foreground">
          Step {currentStep} of {totalSteps}
        </div>
      )}

      {/* Current operation */}
      {currentOperation && (
        <div className="text-sm font-medium text-center max-w-xs animate-pulse">
          {currentOperation}
        </div>
      )}
    </div>
  );
}
